import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import './Auth.css';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

interface LoginUsernameResponse {
  message: string;
  sessionId: string;
  sessionToken: string;
  user: {
    id: string;
    loginName: string;
    displayName: string;
  };
}

interface LoginPasswordResponse {
  message: string;
  token?: string;  // JWT token (if login successful)
  user?: {  // User data (if login successful)
    userId: number;
    username: string;
    email: string;
    role: string;
  };
  sessionId?: string;  // Only if TOTP required
  sessionToken?: string;  // Only if TOTP required
  requiresTOTP: boolean;
}

interface LoginTOTPResponse {
  message: string;
  token: string;  // JWT token
  user: {  // User data
    userId: number;
    username: string;
    email: string;
    role: string;
  };
}

const Login: React.FC = () => {
  const navigate = useNavigate();

  // Multi-step state
  const [step, setStep] = useState<'username' | 'password' | 'totp'>('username');
  const [sessionData, setSessionData] = useState<{
    sessionId: string;
    sessionToken: string;
    userId: string;
    displayName: string;
  } | null>(null);

  // Form data
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');

  // UI state
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const handleUsernameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!username) {
      setError('Username is required');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post<LoginUsernameResponse>(
        `${API_BASE_URL}/auth/custom/login-username`,
        { loginName: username }
      );

      setSessionData({
        sessionId: response.data.sessionId,
        sessionToken: response.data.sessionToken,
        userId: response.data.user.id,
        displayName: response.data.user.displayName,
      });

      setStep('password');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'User not found';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!password) {
      setError('Password is required');
      return;
    }

    if (!sessionData) {
      setError('Session expired. Please start over.');
      setStep('username');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post<LoginPasswordResponse>(
        `${API_BASE_URL}/auth/custom/login-password`,
        {
          sessionId: sessionData.sessionId,
          sessionToken: sessionData.sessionToken,
          password,
        }
      );

      // Update session data
      setSessionData({
        ...sessionData,
        sessionId: response.data.sessionId || '',
        sessionToken: response.data.sessionToken || '',
      });

      if (response.data.requiresTOTP) {
        // MFA required
        setStep('totp');
      } else if (response.data.token && response.data.user) {
        // Login successful - received JWT token
        handleLoginSuccess(response.data.token, response.data.user);
      } else {
        setError('Invalid response from server');
      }
    } catch (err: any) {
      const errorData = err.response?.data;

      // Handle email not verified error
      if (err.response?.status === 403 && errorData?.requiresEmailVerification) {
        navigate('/verify-email', {
          state: {
            userId: errorData.userId,
          },
        });
        return;
      }

      const errorMessage = errorData?.error || 'Invalid password';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleTOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!totpCode) {
      setError('TOTP code is required');
      return;
    }

    if (!sessionData) {
      setError('Session expired. Please start over.');
      setStep('username');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post<LoginTOTPResponse>(
        `${API_BASE_URL}/auth/custom/login-totp`,
        {
          sessionId: sessionData.sessionId,
          sessionToken: sessionData.sessionToken,
          totpCode,
        }
      );

      // Login successful - received JWT token
      handleLoginSuccess(response.data.token, response.data.user);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Invalid TOTP code';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = (token: string, user: any) => {
    // Store JWT token and user data
    localStorage.setItem('authToken', token);
    localStorage.setItem('user', JSON.stringify(user));

    // Navigate to home
    navigate('/');

    // Reload to trigger auth context update
    window.location.reload();
  };

  const handleBack = () => {
    if (step === 'password') {
      setStep('username');
      setPassword('');
      setError('');
    } else if (step === 'totp') {
      setStep('password');
      setTotpCode('');
      setError('');
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Sign In</h1>
          {step === 'username' && <p>Welcome back to PRO Market History</p>}
          {step === 'password' && <p>Hi, {sessionData?.displayName}</p>}
          {step === 'totp' && <p>Two-Factor Authentication</p>}
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Step 1: Username */}
        {step === 'username' && (
          <form onSubmit={handleUsernameSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="username">Username or Email</label>
              <input
                type="text"
                id="username"
                name="username"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setError('');
                }}
                placeholder="Enter your username"
                disabled={loading}
                autoComplete="username"
                autoFocus
                required
              />
            </div>

            <button
              type="submit"
              className="submit-button"
              disabled={loading}
            >
              {loading ? 'Checking...' : 'Continue'}
            </button>

            <button
              type="button"
              onClick={() => navigate('/')}
              className="back-button"
              disabled={loading}
              style={{ marginTop: '10px' }}
            >
              ← Back to Home
            </button>

            <div className="auth-footer">
              <p>
                Don't have an account?{' '}
                <Link to="/register">Create one here</Link>
              </p>
            </div>
          </form>
        )}

        {/* Step 2: Password */}
        {step === 'password' && (
          <form onSubmit={handlePasswordSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                placeholder="Enter your password"
                disabled={loading}
                autoComplete="current-password"
                autoFocus
                required
              />
            </div>

            <button
              type="submit"
              className="submit-button"
              disabled={loading}
            >
              {loading ? 'Verifying...' : 'Sign In'}
            </button>

            <button
              type="button"
              onClick={handleBack}
              className="back-button"
              disabled={loading}
              style={{ marginTop: '10px' }}
            >
              ← Back
            </button>

            <div className="auth-footer" style={{ marginTop: '15px' }}>
              <p>
                <Link to="/forgot-password">Forgot your password?</Link>
              </p>
            </div>
          </form>
        )}

        {/* Step 3: TOTP */}
        {step === 'totp' && (
          <form onSubmit={handleTOTPSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="totpCode">Authentication Code</label>
              <input
                type="text"
                id="totpCode"
                name="totpCode"
                value={totpCode}
                onChange={(e) => {
                  setTotpCode(e.target.value);
                  setError('');
                }}
                placeholder="000000"
                disabled={loading}
                autoComplete="one-time-code"
                autoFocus
                maxLength={6}
                pattern="[0-9]{6}"
                required
              />
              <small style={{ display: 'block', marginTop: '8px', color: '#666' }}>
                Enter the 6-digit code from your authenticator app
              </small>
            </div>

            <button
              type="submit"
              className="submit-button"
              disabled={loading}
            >
              {loading ? 'Verifying...' : 'Verify'}
            </button>

            <button
              type="button"
              onClick={handleBack}
              className="back-button"
              disabled={loading}
              style={{ marginTop: '10px' }}
            >
              ← Back
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;
